<template>
  <button
    v-if="product"
    class="wishlist-button wk-button"
    :class="`wishlist-button--${template}`"
    type="button"
    :data-product-handle="product?.handle"
    :data-variant-id="variant?.id"
    @click.prevent="handleClick"
  >
    <span class="wishlist-button__wishlist-icon">
      <span
        class="visually-hidden"
        v-text="$string('product.card.wishlist.add')"
      />

      <icon-wishlist />
    </span>

    <span
      class="wishlist-button__wishlist-icon-filled"
      :class="$class('hidden')"
    >
      <span
        class="visually-hidden"
        v-text="$string('product.card.wishlist.remove')"
      />

      <icon-wishlist-fill />
    </span>
  </button>
</template>

<script>

/**
 * Vue: Wishlist button (wishlist-button)
 * -----------------------------------------------------------------------------
 * Button to add/remove product from wishlist.
 *
 * @param {Boolean|Object} product - Product object, false shows loading state.
 * @param {String} template - `card`, `form`, or `product`.
 * @param {Boolean|Object} variant - Specified or default variant.
 *
 */
import IconWishlist from '~icons/misc/heart.svg'
import IconWishlistFill from '~icons/misc/heart-filled.svg'
import { convertToHandle } from '~/helpers/convert'

export default {
  name: 'WishlistButton',

  components: {
    IconWishlist,
    IconWishlistFill,
  },

  props: {
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    template: {
      type: String,
      default: 'card',
    },
    variant: {
      type: [Boolean, Object],
      default: false,
    },
  },

  data() {
    return {
      key: 'cnvs-wishlist-data',
    }
  },

  computed: {

    /**
   * Returns discount percentage.
   * @returns {Boolean|Number}
   */
    discountPercentage() {
      const discountBadge = cnvs.badges['discount-badge']
      const hasNoDiscountTag = discountBadge.tag === ''
        ? false
        : this.product?.tags?.some(tag => tag === discountBadge.tag)

      if (hasNoDiscountTag) {
        return false
      }

      const compareAtPrice = this.product?.compareAtPrice?.amount
      const price = this.product?.price?.amount

      if (!compareAtPrice && price < compareAtPrice) {
        return false
      }

      return compareAtPrice && price < compareAtPrice
        ? Math.floor(((compareAtPrice - price) / compareAtPrice) * 100)
        : false
    },

    /**
   * Find matching badge object based on tag.
   * - Filters out those without text values.
   * - Sorts based on priority.
   * @return {Array{}}
   */
    formattedBadges() {
      const discountBadge = cnvs.badges['discount-badge']

      if (this.discountPercentage && discountBadge.enable) {
        return [
          {
            colour: discountBadge.colour,
            text: discountBadge.text.replace('[x]', this.discountPercentage),
          },
        ]
      }

      return this.product?.badges
        .map((badge) => {
          const sanitisedBadge = convertToHandle(badge)
          return cnvs.badges[sanitisedBadge]
        })
        .filter(Boolean)
        .filter((badge) => badge.text)
        .sort((a, b) => {
          return a.priority - b.priority
        })
    },
  },

  methods: {

    /**
     * Save product data to localStorage.
     * - Promo tag and badge data not available in wishlist.
     */
    handleClick() {
      const variantId = this.variant?.id

      const details = {
        productBadges: this.formattedBadges,
        title: this.product?.product_title || this.product?.title,
      }

      const data = {
        [variantId]: details,
      }

      const storage = localStorage.getItem(this.key)

      /**
       * If storage doesn't exist then write directly.
       */
      if (!storage) {
        localStorage.setItem(this.key, JSON.stringify(data))
        return
      }

      /**
       * If it does then update with new data.
       */
      const json = JSON.parse(storage)
      json[variantId] = details
      localStorage.setItem(this.key, JSON.stringify(json))
    },
  },
}
</script>

<style lang="scss">
@import './wishlist-button';
</style>
